import { type ComponentProps, createContext, useContext } from 'react';

import {
	Dialog as ArkDialog,
	type DialogRootProps as ArkDialogRootProps,
	type HTMLArkProps,
	Portal,
	ark
} from '@ark-ui/react';

import type { DrawerSide, DrawerSize } from '../../lib/types';
import { cn } from '../../lib/utils';

type DrawerRootProps = ArkDialogRootProps & {
	side?: DrawerSide;
};

const DrawerSideContext = createContext<DrawerSide>('right');

function DrawerRoot({
	side = 'right',
	lazyMount = true,
	unmountOnExit = true,
	children,
	...props
}: DrawerRootProps) {
	return (
		<ArkDialog.Root lazyMount={lazyMount} unmountOnExit={unmountOnExit} {...props}>
			<DrawerSideContext.Provider value={side}>{children}</DrawerSideContext.Provider>
		</ArkDialog.Root>
	);
}

function DrawerTrigger({ className, ...props }: ComponentProps<typeof ArkDialog.Trigger>) {
	return <ArkDialog.Trigger className={className} {...props} />;
}

type DrawerContentProps = ComponentProps<typeof ArkDialog.Content> & {
	size?: DrawerSize;
};

function DrawerContent({ className, size = 'md', children, ...props }: DrawerContentProps) {
	const side = useContext(DrawerSideContext);
	return (
		<Portal>
			<ArkDialog.Backdrop className="pt-drawer-backdrop" />
			<ArkDialog.Positioner className="pt-drawer-positioner" data-side={side}>
				<ArkDialog.Content
					className={cn('pt-drawer-content', className)}
					data-size={size}
					data-side={side}
					{...props}
				>
					{children}
				</ArkDialog.Content>
			</ArkDialog.Positioner>
		</Portal>
	);
}

function DrawerInnerContent({ className, size = 'md', children, ...props }: DrawerContentProps) {
	const side = useContext(DrawerSideContext);
	return (
		<ArkDialog.Content
			className={cn('pt-drawer-content', className)}
			data-size={size}
			data-side={side}
			{...props}
		>
			{children}
		</ArkDialog.Content>
	);
}

function DrawerBackdrop({ className, ...props }: ComponentProps<typeof ArkDialog.Backdrop>) {
	return <ArkDialog.Backdrop className={cn('pt-drawer-backdrop', className)} {...props} />;
}

function DrawerPositioner({ className, ...props }: ComponentProps<typeof ArkDialog.Positioner>) {
	const side = useContext(DrawerSideContext);
	return (
		<ArkDialog.Positioner
			className={cn('pt-drawer-positioner', className)}
			data-side={side}
			{...props}
		/>
	);
}

function DrawerTitle({ className, ...props }: ComponentProps<typeof ArkDialog.Title>) {
	return <ArkDialog.Title className={cn('pt-drawer-title', className)} {...props} />;
}

function DrawerDescription({ className, ...props }: ComponentProps<typeof ArkDialog.Description>) {
	return <ArkDialog.Description className={cn('pt-drawer-description', className)} {...props} />;
}

function DrawerClose({ className, ...props }: ComponentProps<typeof ArkDialog.CloseTrigger>) {
	return <ArkDialog.CloseTrigger className={cn(className)} {...props} />;
}

function DrawerBody({ className, ...props }: HTMLArkProps<'div'>) {
	return <ark.div className={cn('pt-drawer-body', className)} {...props} />;
}

function DrawerFooter({ className, ...props }: HTMLArkProps<'div'>) {
	return <ark.div className={cn('pt-drawer-footer', className)} {...props} />;
}

const Drawer = Object.assign(DrawerRoot, {
	Root: DrawerRoot,
	Trigger: DrawerTrigger,
	Content: DrawerContent,
	InnerContent: DrawerInnerContent,
	Backdrop: DrawerBackdrop,
	Positioner: DrawerPositioner,
	Title: DrawerTitle,
	Description: DrawerDescription,
	Close: DrawerClose,
	Body: DrawerBody,
	Footer: DrawerFooter
});

export { Drawer, type DrawerRootProps, type DrawerContentProps };
